/**
 * SpeakingSubtitle
 *
 * Global, fixed-position subtitle bar that surfaces the AI's spoken text
 * in real time and a small animated waveform indicator. Mounted once in
 * App.tsx — every component that calls naturalVoiceService.speak() (or its
 * helpers) automatically updates this UI via the speech subscriber.
 *
 * Why this exists:
 *   1. Audio may be muted, off, or still loading. The subtitle delivers
 *      the same content visually so the experience never depends on sound.
 *   2. The "loading" state confirms the system heard the user's tap even
 *      before the TTS audio bytes arrive (network latency).
 *   3. The waveform indicator gives a clear "AI is speaking" affordance,
 *      so children and caregivers know to listen / read.
 */

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  subscribeToSpeech,
  type SpeechEvent,
} from '../../lib/naturalVoiceService';

const INK = '#2a6d5b';
const BRAND_GREEN = '#42a57a';
const GOLD = '#c9a961';
const CREAM_DEEP = '#f4efde';
const SERIF = "'Fraunces', Georgia, serif";
const SANS = "'Inter', system-ui, -apple-system, sans-serif";

/** Three vertical bars that pulse to suggest sound. Pure CSS — no audio analysis. */
function Waveform({ active }: { active: boolean }) {
  const bars = [0, 1, 2, 3];
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 28,
        flexShrink: 0,
      }}
    >
      {bars.map((i) => (
        <span
          key={i}
          style={{
            width: 5,
            height: 28,
            borderRadius: 3,
            background: BRAND_GREEN,
            animation: active
              ? `wp-wave 1s ease-in-out ${i * 0.12}s infinite`
              : undefined,
            transform: active ? undefined : 'scaleY(0.35)',
            transformOrigin: 'center',
            opacity: active ? 1 : 0.45,
          }}
        />
      ))}
    </span>
  );
}

/**
 * Three dots that scale in turn. Used for the "loading" state — same
 * visual language as the waveform but conveys "waiting for audio".
 */
function LoadingDots() {
  const dots = [0, 1, 2];
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
      }}
    >
      {dots.map((i) => (
        <span
          key={i}
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: BRAND_GREEN,
            animation: `wp-dot 1.2s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

export function SpeakingSubtitle() {
  const [event, setEvent] = useState<SpeechEvent>({
    state: 'idle',
    text: null,
    language: null,
  });

  useEffect(() => subscribeToSpeech(setEvent), []);

  const visible = event.state !== 'idle' && !!event.text;
  const speaking = event.state === 'speaking';

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            key="subtitle"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            lang={event.language ?? undefined}
            style={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)',
              zIndex: 10100,
              maxWidth: 'min(880px, calc(100vw - 40px))',
              padding: '22px 32px',
              background: '#ffffff',
              border: `1.5px solid ${GOLD}66`,
              borderRadius: 999,
              boxShadow: '0 16px 44px rgba(31,77,62,0.18)',
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              pointerEvents: 'none',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: CREAM_DEEP,
                flexShrink: 0,
              }}
            >
              {speaking ? <Waveform active /> : <LoadingDots />}
            </span>
            <span
              style={{
                fontFamily: SERIF,
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 'clamp(20px, 2.4vw, 26px)',
                color: INK,
                lineHeight: 1.4,
                // Keep long utterances readable but capped — we line-clamp
                // so the bar never grows into the content beneath it.
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {event.text}
            </span>
            {!speaking && (
              <span
                style={{
                  fontFamily: SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: '#9a9a9a',
                  flexShrink: 0,
                }}
              >
                {event.language === 'es' ? 'Cargando' : 'Loading'}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pulse keyframes — defined once with the component so the host page
          doesn't need to know about them. */}
      <style>{`
        @keyframes wp-wave {
          0%, 100% { transform: scaleY(0.35); }
          50%      { transform: scaleY(1); }
        }
        @keyframes wp-dot {
          0%, 100% { transform: scale(0.6); opacity: 0.4; }
          50%      { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </>
  );
}
